import { c as _c } from "react/compiler-runtime";
// `<OgClamp>` — multi-line text clamp for OG cards.
//
// Satori supports `display: -webkit-box` + `-webkit-line-clamp` for
// truncating long text with an ellipsis. Use this whenever the content
// can come from user-supplied strings (market titles, thesis quotes,
// gift messages) so a 200-character submission doesn't blow the layout.
//
// IMPORTANT — Satori child rule:
// Satori validates that any `<div>` with multiple children declares
// `display: flex` or `display: none`. Because line-clamp requires
// `display: -webkit-box`, the clamp wrapper can't be flex. So this
// primitive only accepts a STRING child — never an element — and
// embeds typography styles directly on the wrapper. That way Satori
// always sees the clamp div with exactly one (text) child.
//
// Use the `variant` prop to pick the typography preset; `heading` and
// `body` cover the two practical clamp cases. Override colour /
// fontStyle / maxWidth as needed for surface-specific tweaks.

import type { CSSProperties, JSX } from 'react';
import { OG_TOKENS } from '../tokens';
export type OgClampVariant = 'heading' | 'body' | 'kpi';
export interface OgClampProps {
  /** Maximum number of lines before truncating with "...". */
  lines: number;
  /** Typography preset. Defaults to `body`. */
  variant?: OgClampVariant;
  /** Override the default colour. */
  color?: string;
  /** Italic / regular. Use `'italic'` for thesis quotes etc. */
  fontStyle?: 'normal' | 'italic';
  /** Optional max width — by default the clamp fills its parent. */
  maxWidth?: number | string;
  /** Extra inline styles merged onto the wrapper. */
  style?: CSSProperties;
  /** RAW string only. Pass interpolated text directly — never wrap an
   *  element here. Satori cannot validate a non-flex div with element
   *  children. */
  children: string | number;
}
const VARIANT_STYLES: Record<OgClampVariant, CSSProperties> = {
  heading: {
    fontSize: 48,
    fontWeight: 700,
    letterSpacing: -0.5,
    lineHeight: 1.15,
    color: OG_TOKENS.gray50
  },
  body: {
    fontSize: 28,
    fontWeight: 400,
    lineHeight: 1.4,
    color: OG_TOKENS.gray50,
    opacity: 0.85
  },
  kpi: {
    fontSize: 96,
    fontWeight: 700,
    letterSpacing: -1.5,
    lineHeight: 1.05,
    color: OG_TOKENS.gray50
  }
};
export function OgClamp(props) {
  const $ = _c(16);
  const variant = props.variant ?? "body";
  const variantStyle = VARIANT_STYLES[variant];
  let t0;
  if ($[0] !== props.color) {
    t0 = props.color !== undefined ? {
      color: props.color
    } : {};
    $[0] = props.color;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.fontStyle) {
    t1 = props.fontStyle !== undefined ? {
      fontStyle: props.fontStyle
    } : {};
    $[2] = props.fontStyle;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.maxWidth) {
    t2 = props.maxWidth !== undefined ? {
      maxWidth: props.maxWidth
    } : {};
    $[4] = props.maxWidth;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.lines || $[7] !== props.style || $[8] !== t0 || $[9] !== t1 || $[10] !== t2 || $[11] !== variantStyle) {
    t3 = {
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: props.lines,
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...variantStyle,
      ...t0,
      ...t1,
      ...t2,
      ...props.style
    };
    $[6] = props.lines;
    $[7] = props.style;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = variantStyle;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const wrapperStyle = t3;
  let t4;
  if ($[13] !== props.children || $[14] !== wrapperStyle) {
    t4 = <div style={wrapperStyle}>{props.children}</div>;
    $[13] = props.children;
    $[14] = wrapperStyle;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}